import { buildNormalizedOrdersFromCategories } from "../../utils/orders/normalizeOrders";
import { formatHrBookingRef } from "../../utils/bookingRefDisplay.js";
import { fetchAgentOrdersBundleForOperationRequest } from "./fetchAgentOrdersBundleForOperationRequest.js";
import {
  isOperationRequestCancelledOrder,
  normalizeSearchToken,
  orderMatchesOperationRequestQuery,
  traceOrderSearchMatch,
} from "./operationRequestSearchMatch.js";
import { fetchOperationRequestOrderByBookingRef } from "./fetchOperationRequestOrderByBookingRef.js";
import {
  logOperationRequestSearchPoolTrace,
  logOperationRequestSearchTargetTrace,
} from "./operationRequestSearchTrace.js";

/** סף תווים לפני הרצת חיפוש (אין קריאת רשת מתחת לסף זה). */
export const MIN_OPERATION_REQUEST_QUERY_CHARS = 3;

function enrichNormalizedOrdersForOperationRequestSearch(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((o) => {
    if (!o || typeof o !== "object") return o;
    const hr = formatHrBookingRef(o.bookingRef);
    if (!hr) return o;
    if (o.displayOrderNumber != null && String(o.displayOrderNumber).trim() !== "") return o;
    return { ...o, displayOrderNumber: hr };
  });
}

function filterNormalizedOrdersByQuery(orders, rawQuery) {
  const needle = normalizeSearchToken(rawQuery);
  if (!needle) return Array.isArray(orders) ? orders : [];

  const list = Array.isArray(orders) ? orders : [];
  const matched = [];
  let firstTrace = null;

  for (const o of list) {
    if (!orderMatchesOperationRequestQuery(o, needle)) continue;
    matched.push(o);
    if (!firstTrace) {
      firstTrace = traceOrderSearchMatch(o, needle);
    }
  }

  if (firstTrace?.matched) {
    console.log("[OP_REQUEST_SEARCH_CANDIDATE_MATCH_TRACE]", {
      query: String(rawQuery ?? "").trim(),
      normalizedNeedle: needle,
      matchedField: firstTrace.matchedField,
      matchedToken: firstTrace.matchedToken,
      orderId: matched[0]?.orderId ?? null,
      bookingRef: matched[0]?.bookingRef ?? null,
      displayOrderNumber: matched[0]?.displayOrderNumber ?? null,
      status: matched[0]?.status ?? null,
    });
  }

  return matched;
}

function logCancelledSearchInclusion(rawQuery, normalizedPool, results) {
  const pool = Array.isArray(normalizedPool) ? normalizedPool : [];
  const out = Array.isArray(results) ? results : [];
  const poolCancelled = pool.filter(isOperationRequestCancelledOrder).length;
  const resultsCancelled = out.filter(isOperationRequestCancelledOrder).length;

  console.log("[OP_REQUEST_SEARCH_CANCELLED_INCLUDED]", {
    query: String(rawQuery ?? "").trim(),
    poolTotal: pool.length,
    poolCancelled,
    resultsTotal: out.length,
    resultsCancelled,
    excludesCancelledByClientFilter: false,
  });
}

/**
 * חיפוש הזמנות לפנייה לאופרציה — סינון client-side על bundle (רק אחרי הקלדה ≥ סף).
 * כולל הזמנות מבוטלות; תומך ב-HR-XXXXXXXX / HRXXXXXXXX / ספרות בלבד.
 *
 * @param {string} query
 * @param {object} ldsOrdersGuardCtx — כמו ב־Orders.jsx
 * @param {{ minQueryLength?: number, preloadedNormalized?: unknown[] | null }} [options]
 */
export async function searchAgentOrdersForOperationRequest(query, ldsOrdersGuardCtx, options = {}) {
  const q = String(query ?? "").trim();
  const minLen =
    typeof options.minQueryLength === "number" && options.minQueryLength >= 2
      ? options.minQueryLength
      : MIN_OPERATION_REQUEST_QUERY_CHARS;

  if (q.length < minLen) {
    return {
      orders: [],
      skipped: true,
      reason: "min_query_length",
      query: q,
      normalizedSnapshot: null,
    };
  }

  const normalizedNeedle = normalizeSearchToken(q);
  console.log("[OP_REQUEST_SEARCH_NORMALIZED_QUERY]", {
    rawQuery: q,
    normalizedNeedle,
  });

  const pre = options.preloadedNormalized;
  const reusedPreload = Array.isArray(pre);

  let normalized;
  if (reusedPreload) {
    normalized = enrichNormalizedOrdersForOperationRequestSearch(pre);
  } else {
    const bundle = await fetchAgentOrdersBundleForOperationRequest(ldsOrdersGuardCtx, null, {
      forSearch: true,
    });
    normalized = enrichNormalizedOrdersForOperationRequestSearch(
      buildNormalizedOrdersFromCategories(bundle)
    );
  }

  logOperationRequestSearchPoolTrace(q, normalizedNeedle, normalized);
  logOperationRequestSearchTargetTrace(q, normalizedNeedle, normalized);

  let orders = filterNormalizedOrdersByQuery(normalized, q);

  if (orders.length === 0 && /^\d{6,}$/.test(normalizedNeedle)) {
    const fallback = await fetchOperationRequestOrderByBookingRef(q);
    if (fallback && orderMatchesOperationRequestQuery(fallback, normalizedNeedle)) {
      orders = [fallback];
      console.log("[OP_REQUEST_SEARCH_BOOKING_REF_FALLBACK_MATCH]", {
        query: q,
        orderId: fallback.orderId,
        displayOrderNumber: fallback.displayOrderNumber,
      });
    }
  }

  logCancelledSearchInclusion(q, normalized, orders);

  return {
    orders,
    skipped: false,
    query: q,
    normalizedSnapshot: normalized,
    meta: {
      source: "client_bundle_filter",
      reusedPreload,
      normalizedNeedle,
    },
  };
}
